import type {
	ItemPromoHeadline,
	PromoCandidate,
} from '@supply-mover-2-0/shared';

/**
 * Catalog/list-context promo headline math — pure helper used by
 * `PromoPricingService.getCatalogPromoHeadlinesForItems` to derive the
 * "best" advertisable LINE-scope promo per item from a pre-filtered list
 * of eligible candidates.
 *
 * "Best" = largest advertisable savings the customer would see on the
 * badge. Tie-break by `priority` (higher first), then by promo id (lower
 * first) for stability. BXGY discountValue is null (the actual saving
 * depends on the GET item's price), but BXGY is ranked behind PERCENT/
 * DOLLARS/FIXED_PRICE so an item that's a qualifier for both still shows
 * the explicit-discount headline.
 */

/**
 * True iff this candidate has at least one BUY-role item slot pointing at
 * `itemId` — either explicit (promo.items), via item-group membership
 * (promo.itemGroups[*].memberItemIds), or via category match
 * (promo.categories[*].categoryName === categoryName). Mirrors
 * `isInBuySet` from item-eligibility.ts but without a cart-line context
 * (we don't have one at catalog time).
 */
export function promoTargetsItem(
	promo: PromoCandidate,
	itemId: number,
	itemGroupIds: Array<number>,
	categoryName: string | null,
): boolean {
	for (const item of promo.items) {
		if (item.role === 'BUY' && item.itemId === itemId) {
			return true;
		}
	}
	for (const group of promo.itemGroups) {
		if (group.role !== 'BUY') {
			continue;
		}
		if (itemGroupIds.includes(group.groupId)) {
			return true;
		}
		if (group.memberItemIds.includes(itemId)) {
			return true;
		}
	}
	if (categoryName != null && categoryName !== '') {
		for (const cat of promo.categories) {
			if (cat.role === 'BUY' && cat.categoryName === categoryName) {
				return true;
			}
		}
	}
	return false;
}

/**
 * Pre-rendered short badge label. The CRM list-builder badge field and
 * the storefront product-card badge both consume this directly so the
 * customer-visible text is centralized here.
 */
export function renderBadgeText(promo: PromoCandidate): string {
	switch (promo.discountType) {
		case 'PERCENT':
			return `${formatNumber(promo.discountValue)}% OFF`;
		case 'DOLLARS':
			return `$${formatNumber(promo.discountValue)} OFF`;
		case 'FIXED_PRICE':
			return `$${formatNumber(promo.discountValue)}`;
		case 'BUY_X_GET_Y': {
			const buy = promo.buyQty ?? 1;
			const get = promo.getQty ?? 1;
			if (promo.getDiscountType === 'FREE') {
				return `BUY ${buy} GET ${get} FREE`;
			}
			return `BUY ${buy} GET ${get}`;
		}
		default:
			return 'ON SALE';
	}
}

function formatNumber(value: number): string {
	if (Number.isInteger(value)) {
		return String(value);
	}
	return String(Math.round(value * 100) / 100);
}

/**
 * Rank score for tie-breaking when multiple promos target the same item.
 * Higher = more advertisable. Used to pick the "best" headline.
 *
 * Ordering rationale:
 *   - Explicit % / $ / fixed-price discounts win over BXGY because the
 *     customer can read the savings directly from the badge.
 *   - Within explicit discounts, larger savings wins (PERCENT compared
 *     against DOLLARS via a rough $/% baseline isn't exact, but at
 *     catalog browse time we don't know the actual line subtotal —
 *     fall back to numeric comparison and trust admin to set sensible
 *     priorities).
 *   - Final tie-break: promo.priority (higher wins), then lower id.
 */
export function rankHeadline(promo: PromoCandidate): number {
	switch (promo.discountType) {
		case 'PERCENT':
			return 30_000 + promo.discountValue;
		case 'DOLLARS':
			return 20_000 + promo.discountValue;
		case 'FIXED_PRICE':
			return 10_000;
		case 'BUY_X_GET_Y':
			return 1_000;
		default:
			return 0;
	}
}

export function toItemPromoHeadline(promo: PromoCandidate): ItemPromoHeadline {
	const isBxgy = promo.discountType === 'BUY_X_GET_Y';
	return {
		promoId: promo.id,
		promoName: promo.name,
		headline: promo.headline,
		badgeText: renderBadgeText(promo),
		discountType: promo.discountType,
		discountValue: isBxgy ? null : promo.discountValue,
		displayBadgeInLists: promo.displayBadgeInLists,
		displayOnItemDetail: promo.displayOnItemDetail,
	};
}

/**
 * Compute the post-promo unit price for catalog/item-detail display
 * (strikethrough preview), given a single-line headline. Returns null
 * when:
 *   - the discount type doesn't lend itself to a single-line strike-
 *     through (BXGY savings depend on the GET item's price, not this
 *     line),
 *   - the headline lacks a numeric value (defensive),
 *   - the original price is zero or negative.
 *
 * Mirrors the line-level math the engine uses (see `calculateLine
 * Discount`) but operates on the lighter `ItemPromoHeadline` shape so
 * the storefront catalog can render strikethrough pricing without
 * having to load the full `PromoCandidate`.
 */
export function applyHeadlineToUnitPrice(
	unitPrice: number,
	headline: ItemPromoHeadline,
): number | null {
	if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
		return null;
	}
	if (headline.discountValue == null) {
		return null;
	}
	switch (headline.discountType) {
		case 'PERCENT': {
			const fraction = Math.min(Math.max(headline.discountValue / 100, 0), 1);
			return round2(unitPrice * (1 - fraction));
		}
		case 'DOLLARS': {
			return Math.max(0, round2(unitPrice - headline.discountValue));
		}
		case 'FIXED_PRICE': {
			// FIXED_PRICE = "the new price", capped to never inflate above
			// the original (a misconfigured promo with FIXED_PRICE > original
			// is probably an admin error and shouldn't visibly raise the
			// customer-facing price).
			return Math.min(unitPrice, round2(headline.discountValue));
		}
		case 'BUY_X_GET_Y':
			// BXGY savings depend on the GET item's price, not the BUY
			// item's. Per-item strikethrough doesn't apply.
			return null;
		default:
			return null;
	}
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

/**
 * Pick the best-fit headline for a single item from a list of pre-filtered
 * eligible candidates. Returns null when no candidate targets the item.
 *
 * "Best" here is the *advertising* rank — the headline the catalog card
 * should advertise to drive conversion. PERCENT > DOLLARS > FIXED_PRICE >
 * BXGY, larger value first. This is fast (no per-item price math) which
 * matters when ranking across hundreds of catalog tiles per render.
 *
 * For the product-detail page — where we want the chip to name the promo
 * the *cart engine will actually apply* — use `pickEngineAppliedHeadline`
 * instead. That picker computes per-promo post-base prices and ranks by
 * the lowest result, matching what `priceCart` would pick at qty=1.
 */
export function pickBestHeadline(
	candidates: Array<PromoCandidate>,
	itemId: number,
	itemGroupIds: Array<number>,
	categoryName: string | null,
): ItemPromoHeadline | null {
	let best: PromoCandidate | null = null;
	let bestScore = -Infinity;
	for (const promo of candidates) {
		if (!promoTargetsItem(promo, itemId, itemGroupIds, categoryName)) {
			continue;
		}
		const score = rankHeadline(promo);
		if (score > bestScore) {
			best = promo;
			bestScore = score;
			continue;
		}
		if (score === bestScore && best != null) {
			// Stable tie-break: priority desc, then id asc.
			if (
				promo.priority > best.priority ||
				(promo.priority === best.priority && promo.id < best.id)
			) {
				best = promo;
			}
		}
	}
	return best == null ? null : toItemPromoHeadline(best);
}

/**
 * Pick the headline that names the promo the engine *will actually apply*
 * to a single line at qty=1 of this item, given the customer's effective
 * base price (contract or list, whichever is in effect).
 *
 * Used by the product-detail page so the chip / strikethrough / final-
 * price math matches what the cart shows when the customer adds the item
 * — fixing the long-standing UX confusion where the detail page
 * advertised "10% OFF Seafood Counter" but the cart applied "Fresh Catch
 * Sale" to the same line.
 *
 * Algorithm:
 *   1. Filter to candidates that target the item (same as `pickBestHeadline`).
 *   2. For each, compute the post-promo unit price with `applyHeadlineToUnitPrice`.
 *   3. Pick the candidate that yields the LOWEST resulting price (largest
 *      saving). Tiebreak by priority desc → id asc, like the legacy picker.
 *   4. BXGY targets (discountValue=null when applied per-line) skip the
 *      price comparison — they fall back to the legacy picker so a
 *      "BUY 2 GET 1 FREE" badge still surfaces when no price-reducing
 *      promo applies.
 *
 * NOTE: this does not yet model promo *stacking* (multiple stackable
 * promos applying simultaneously to the same line). The cart engine's
 * `priceCart` is the source of truth for stacked outcomes; if stacking
 * starts producing visible mismatches, swap this picker for an
 * actual `priceCart` round-trip on the API side.
 */
export function pickEngineAppliedHeadline(
	candidates: Array<PromoCandidate>,
	itemId: number,
	itemGroupIds: Array<number>,
	categoryName: string | null,
	basePrice: number,
): ItemPromoHeadline | null {
	if (!Number.isFinite(basePrice) || basePrice <= 0) {
		// Without a usable base price we can't rank by post-promo price —
		// fall back to advertising rank so the chip still appears.
		return pickBestHeadline(candidates, itemId, itemGroupIds, categoryName);
	}

	let best: PromoCandidate | null = null;
	let bestFinal = basePrice; // any winning promo must beat the base

	for (const promo of candidates) {
		if (!promoTargetsItem(promo, itemId, itemGroupIds, categoryName)) {
			continue;
		}
		// Build a transient headline so we can reuse the canonical math
		// helper. We only care about the resulting unit price here.
		const final = applyHeadlineToUnitPrice(
			basePrice,
			toItemPromoHeadline(promo),
		);
		if (final == null) {
			// BXGY (returns null) — handled by the fallback below.
			continue;
		}
		if (final < bestFinal) {
			best = promo;
			bestFinal = final;
			continue;
		}
		if (final === bestFinal && best != null) {
			if (
				promo.priority > best.priority ||
				(promo.priority === best.priority && promo.id < best.id)
			) {
				best = promo;
			}
		}
	}

	if (best != null) {
		return toItemPromoHeadline(best);
	}
	// No price-reducing applied promo (or only BXGY targets matched).
	// Fall back to advertising rank so the badge still surfaces.
	return pickBestHeadline(candidates, itemId, itemGroupIds, categoryName);
}
